/**
 * Catálogo de eventos analíticos para la web del cliente (ue-web).
 *
 * Convenciones:
 *   - Nombres en `snake_case`.
 *   - Para eventos del catálogo estándar de GA4 (Enhanced E-commerce)
 *     usamos exactamente los nombres reconocidos por Google — así los
 *     reportes "Monetization", "Funnels" y "Audience" se arman solos
 *     sin configuración custom.
 *     Lista oficial:
 *     https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 *   - Eventos custom específicos de Urban Eggs van en su propio bloque
 *     y se documentan con su propósito + payload esperado.
 *   - Properties en `snake_case` (convención GA4) — distinto del
 *     `camelCase` que usábamos con PostHog.
 */
export const ANALYTICS_EVENTS = {
  // ─── E-commerce (GA4 recommended) ────────────────────────────────
  // Estos disparan reportes automáticos de Monetization en GA4.
  VIEW_ITEM_LIST: "view_item_list",
  VIEW_ITEM: "view_item",
  ADD_TO_CART: "add_to_cart",
  REMOVE_FROM_CART: "remove_from_cart",
  VIEW_CART: "view_cart",
  BEGIN_CHECKOUT: "begin_checkout",
  ADD_SHIPPING_INFO: "add_shipping_info",
  ADD_PAYMENT_INFO: "add_payment_info",
  PURCHASE: "purchase",
  REFUND: "refund",

  // ─── Identity (GA4 recommended) ──────────────────────────────────
  LOGIN: "login",
  SIGN_UP: "sign_up",

  // ─── Saved cards / payment methods vault (custom) ────────────────
  PAYMENT_METHODS_VIEWED: "payment_methods_viewed",
  CARD_ADD_STARTED: "card_add_started",
  CARD_ADDED: "card_added",
  CARD_DELETED: "card_deleted",
  CARD_SET_DEFAULT: "card_set_default",

  // ─── Orders history (custom) ─────────────────────────────────────
  ORDERS_LIST_VIEWED: "orders_list_viewed",
  ORDER_DETAIL_VIEWED: "order_detail_viewed",

  // ─── Subscriptions / weekly box (custom Urban Eggs) ──────────────
  // Conversión clave del negocio — un cliente con suscripción vale
  // 4×–12× más LTV que uno transaccional.
  SUBSCRIPTION_VIEWED: "subscription_viewed",
  SUBSCRIPTION_STARTED: "subscription_started",
  SUBSCRIPTION_BOX_UPDATED: "subscription_box_updated",
  SUBSCRIPTION_DELIVERY_UPDATED: "subscription_delivery_updated",
  SUBSCRIPTION_PAUSED: "subscription_paused",
  SUBSCRIPTION_RESUMED: "subscription_resumed",
  SUBSCRIPTION_CANCELED: "subscription_canceled",

  // ─── Guest checkout vs auth (custom) ─────────────────────────────
  // Diferenciar funnel "comprar sin cuenta" vs "comprar logueado"
  // para entender qué tanto cuesta el friction de auth.
  GUEST_CHECKOUT_STARTED: "guest_checkout_started",

  // ─── Trazabilidad QR (custom Urban Eggs) ─────────────────────────
  // Cuando el cliente escanea el QR de un huevo y aterriza en /trace.
  // Métrica clave de la promesa de marca "trazable hasta el productor".
  QR_TRACE_VIEWED: "qr_trace_viewed",

  // ─── Auth (custom) ───────────────────────────────────────────────
  USER_LOGGED_OUT: "user_logged_out",

  // ─── Funnel detail (custom — complementan a los GA4 estándar) ────
  // GA4 NO trackea fallas de pago por defecto; las necesitamos para
  // medir tasa de conversion del checkout y debuggear pérdidas.
  ORDER_PAYMENT_FAILED: "order_payment_failed",
  // Custom para medir cuando un cliente abandona — el `clear cart`
  // explicit es señal de "cambié de idea" vs solo navegar fuera.
  CART_CLEARED: "cart_cleared",
  // Custom — disparado cuando el cliente entra al cart vía el link de
  // la recovery email (URL con `?recovered=1`). Mide la efectividad
  // del email de abandono sin necesidad de correlación backend: el
  // funnel cart_recovered → begin_checkout → purchase en GA4 te da
  // la tasa de conversión por recovery sin tocar la base de datos.
  CART_RECOVERED: "cart_recovered",
  // Custom — disparado cuando el cliente entra al checkout (begin_checkout
  // ya disparó) pero sale sin completar la compra. Se emite en `beforeunload`
  // del componente CheckoutForm/GuestCheckoutForm. Útil para medir el
  // drop-off entre paso 2 (checkout) y paso 3 (purchase) de forma
  // explícita en lugar de inferirlo de la ausencia de PURCHASE.
  CHECKOUT_ABANDONED: "checkout_abandoned",
} as const;

export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/**
 * Rutas donde NUNCA debe inyectarse `gtag('event', 'page_view', …)`
 * automático ni cargar pixels de terceros. Hoy aplica a las pantallas
 * de pago donde Stripe Elements muestra iframes con datos de tarjeta
 * — GA enmascararía los iframes igual, pero apagamos el page_view
 * de la ruta por completo para no enviar URLs con tokens.
 */
export const ANALYTICS_NO_TRACK_PATHS: readonly string[] = [
  "/checkout",
  "/profile/payment-methods",
];

/**
 * Shape estándar de un "item" de e-commerce en GA4. Mandar items
 * con esta forma exacta es lo que hace que el reporte "Items" en GA4
 * se pueble correctamente — es la causa #1 de "el dashboard de
 * productos no muestra nada" cuando empiezas con GA4.
 */
export interface GA4Item {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  currency?: string;
}

/**
 * Constructor type-safe — fuerza que cada lugar que arme items para
 * un evento de cart/checkout/purchase use el mismo shape, evitando
 * que un componente nuevo envíe `productId` en lugar de `item_id`
 * y rompa silenciosamente los reportes.
 */
export function buildGA4Item(input: {
  id: string;
  name: string;
  category?: string;
  variant?: string;
  price?: number;
  quantity?: number;
}): GA4Item {
  return {
    item_id: input.id,
    item_name: input.name,
    item_category: input.category,
    item_variant: input.variant,
    price: input.price,
    quantity: input.quantity,
    currency: "MXN",
  };
}
